import { ConsumerUnit } from 'entities/ConsumerUnit';
import { Invoice } from 'entities/Invoice';

/** Fatura com aviso de leitura do medidor inconsistente. */
export const hasMeterReadingWarning = (
  invoice: Pick<Invoice, 'readingWarnings'>
): boolean =>
  !!invoice.readingWarnings?.some((w) => w.type === 'meter_reading_suspect');

/** Consumo faturado (só componente TE). */
export const billedConsumptionKwh = (
  invoice: Pick<Invoice, 'suppliedEnergy'>
): number => {
  const se = invoice.suppliedEnergy;
  return (
    (se.suppliedEnergyTEFP || 0) +
    (se.suppliedEnergyTE_P || 0) +
    (se.suppliedEnergyTE_Int || 0)
  );
};

/**
 * Consumo do período para os agregados do dashboard (Resumo, card da UC e
 * Média de consumo): kWh FATURADO somando os postos (FP + Ponta +
 * Intermediário); fatura antiga sem os postos extraídos cai no delta do medidor.
 * Até 30/set/2026 o card da UC e a média usavam só o delta do medidor, que
 * ignora Ponta/Intermediário e o fator de perdas do Grupo A — os três números
 * do dashboard não batiam entre si.
 */
export const periodConsumptionKwh = (
  invoice: Pick<Invoice, 'suppliedEnergy'>
): number => {
  const billed = billedConsumptionKwh(invoice);
  if (billed > 0) return billed;
  const se = invoice.suppliedEnergy;
  return (se.currentRead - se.previousRead) * se.factorRead;
};

/**
 * Autoconsumo solar do mês (geração − injeção, nunca negativo) pelas séries
 * mensais da UC. Sem geração ou injeção do mês → 0 (mesma regra do histórico
 * de consumo do relatório e da média do dashboard).
 */
export const selfConsumptionKwh = (
  consumerUnit: Pick<
    ConsumerUnit,
    'monthGenerationList' | 'monthInjectedEnergyList'
  >,
  refMonth: string
): number => {
  const generation = consumerUnit.monthGenerationList?.find(
    (x) => x.refMonth === refMonth
  )?.value;
  const injected = consumerUnit.monthInjectedEnergyList?.find(
    (x) => x.refMonth === refMonth
  )?.value;
  if (generation == null || injected == null) return 0;
  return Math.max(0, generation - injected);
};

/**
 * Consumo TOTAL do mês, qualquer que seja a fonte: kWh da distribuidora
 * (`periodConsumptionKwh`) + autoconsumo solar. É o "Energia total consumida"
 * do Resumo e a base da Média de consumo do dashboard (30/set/2026 — antes o
 * Resumo mostrava só o kWh da distribuidora e a média somava o autoconsumo).
 */
export const totalConsumptionKwh = (
  invoice: Pick<Invoice, 'suppliedEnergy' | 'refMonth'>,
  consumerUnit?: Pick<
    ConsumerUnit,
    'monthGenerationList' | 'monthInjectedEnergyList'
  >
): number =>
  periodConsumptionKwh(invoice) +
  (consumerUnit ? selfConsumptionKwh(consumerUnit, invoice.refMonth) : 0);

/**
 * Energia FORNECIDA no ciclo (out/2026, pedido do Bruno): o kWh que a
 * distribuidora faturou, somando os postos (FP + Ponta + Intermediário) — é o
 * consumo exibido no relatório e nas listas de fatura. Antes o relatório usava o
 * consumo reconstruído pelo motor a partir das LEITURAS do medidor
 * (`virtualSuppliedEnergy.totalConsumptionEnergy`) e as listas o delta do
 * medidor — com leitura inconsistente (troca de medidor, leitura que não
 * continua a do mês anterior) o número exibido não era o consumo cobrado. O
 * aviso de leitura inconsistente continua aparecendo à parte. Fatura antiga sem
 * os postos extraídos cai no valor do motor.
 */
export const suppliedConsumptionKwh = (
  invoice: Pick<Invoice, 'virtual' | 'suppliedEnergy'>
): number => {
  const billed = invoice.suppliedEnergy ? billedConsumptionKwh(invoice) : 0;
  if (billed > 0) return billed;
  return invoice.virtual?.virtualSuppliedEnergy?.totalConsumptionEnergy || 0;
};

/**
 * "Energia total consumida" da seção Relação consumo vs. geração do relatório:
 * consumo da distribuidora em TODOS os postos + autoconsumo solar, ambos do
 * motor. O `totalEnergyConsumptionOnPeriod` do backend soma só o Fora Ponta —
 * na Escola Comunitária FEV/26 dava 26.817 em vez de 18.438 + 11.828 = 30.266, e
 * a tabela não fechava com a linha "consumida da distribuidora" logo acima.
 */
export const reportTotalConsumptionKwh = (
  invoice: Pick<Invoice, 'virtual' | 'suppliedEnergy'>
): number =>
  suppliedConsumptionKwh(invoice) +
  (invoice.virtual?.virtualGenerationEnergy
    .totalSolarEnergyConsumptionOnPeriod || 0);

/**
 * Consumo a exibir nas listas de fatura: a energia fornecida (faturada, todos
 * os postos), não o delta do medidor (out/2026, pedido do Bruno). O ícone de
 * leitura inconsistente segue ao lado do número quando há o aviso.
 */
export const displayConsumptionKwh = (
  invoice: Pick<Invoice, 'suppliedEnergy' | 'readingWarnings'>
): number => periodConsumptionKwh(invoice);
